import { FieldPrimaryListDto } from "@common/dto/field/list-primary-dto";
import { FolderContext } from "@common/dto/folder/context";
import { FolderPrimaryListDto } from "@common/dto/folder/primary-list-dto";
import { useMessage } from "@common/hooks/message";
import { trDts } from "@modules/datasheets/utils/i18n";
import React from "react";
import { FieldPickerField } from "./components/fields/fields.types";
import { getUniqueFieldCombination } from "@common/utils/fields";

export type FieldPickerProps = {
  dndType: string;
  selection: {
    items: Array<FieldPickerField>;
    onChange: (selectedItems: Array<FieldPickerField>) => void;
  };
  isFieldAdded: (field: FieldPickerField) => boolean;
  className?: string;
  getFolders: () => Promise<Array<FolderPrimaryListDto>>;
  getFields: (params: { folderId?: FolderPrimaryListDto['id']; searchQuery?: string; context: number }) => Promise<Array<FieldPrimaryListDto>>;
  disabled: boolean;
};

export const useFieldPickerController = (props: FieldPickerProps) => {
  const { getFolders, getFields } = props;
  const {
    messageProps,
    showErrorMessage,
    closeMessage
  } = useMessage();
  const [folders, setFolders] = React.useState<Array<FolderPrimaryListDto>>([]);
  const [selectedFolder, setSelectedFolder] = React.useState<FolderPrimaryListDto | null>(null);
  const [fields, setFields] = React.useState<Array<FieldPrimaryListDto>>([]);
  const [fieldsLoading, setFieldsLoading] = React.useState<boolean>(false);
  const [expanded, setExpanded] = React.useState(true);

  const toggleExpanded = React.useCallback(() => {
    setExpanded(x => !x);
  }, []);

  const initialize = async () => {
    try {
      const folders = await getFolders();
      setFolders(folders);
    } catch (err) {
      console.error(err);
      showErrorMessage(trDts('Failed to fetch folders'));
    }
  };

  React.useEffect(() => {
    initialize();
  }, []);

  const handleFolderChange = React.useCallback(async (id: FolderPrimaryListDto['id'], folder: FolderPrimaryListDto) => {
    setSelectedFolder(folder);
    setFieldsLoading(true);
    try {
      const fields = await getFields({ folderId: folder.id, context: FolderContext.individual });
      setFields(fields);
    } catch (err) {
      console.error(err);
      showErrorMessage(trDts('Failed to fetch fields'));
    } finally {
      setFieldsLoading(false);
    }
  }, [getFields]);

  const handleSearch = React.useCallback(async ({ searchKeyword }: { searchKeyword: string }) => {
    setFieldsLoading(true);
    try {
      const fields = await getFields({ folderId: selectedFolder?.id, searchQuery: searchKeyword, context: FolderContext.individual });
      setFields(fields);
    } catch (err) {
      console.error(err);
      showErrorMessage(trDts('Failed to fetch fields'));
    } finally {
      setFieldsLoading(false);
    }
  }, [getFields, selectedFolder?.id]);

  const fieldsProcessed = React.useMemo(() => {
    return fields.map((field) => ({
      id: getUniqueFieldCombination({
        id: field.id,
        context: field.context,
        system: field.system,
        type: field.type,
      }),
      name: field.name,
      original: field,
    }));
  }, [fields]);

  return {
    fieldsLoading,
    folders,
    selectedFolder,
    fieldsProcessed,
    handleSearch,
    handleFolderChange,
    closeMessage,
    messageProps,
    expanded,
    toggleExpanded,
  };
};